import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { FinancialYear, AuditLogEntry } from '../types';

// ── Financial Years ──────────────────────────────────────────────────────────

export function useFinancialYears() {
  return useQuery({
    queryKey: ['accounts', 'financial-years'],
    queryFn: async () => {
      const { data } = await api.get<FinancialYear[]>('/api/accounts/financial-years');
      return data;
    },
  });
}

export function useCreateFinancialYear() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { label: string; startDate: string; endDate: string }) => {
      const { data } = await api.post('/api/accounts/financial-years', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'financial-years'] }),
  });
}

export function useLockFinancialYear() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/accounts/financial-years/${id}/lock`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'financial-years'] }),
  });
}

// ── Audit Log ────────────────────────────────────────────────────────────────

export function useAuditLog(filters?: any) {
  return useQuery({
    queryKey: ['accounts', 'audit-log', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.entityType) params.append('entityType', filters.entityType);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.limit) params.append('limit', filters.limit);
      
      const { data } = await api.get<AuditLogEntry[]>(`/api/accounting/audit-logs?${params.toString()}`);
      return data;
    },
  });
}
